<template>
  <div class="flex flex-col gap-4">
    <div class="flex items-center justify-between">
      <!--tabbar-->
      <slot />
      <!--search panel-->
      <div class="flex items-center gap-2">
        <div class="relative">
          <el-input
            placeholder="Search..."
            prefix-icon="el-icon-search"
            class="w-[250px]"
            clearable
            v-model="searchText"
          />
        </div>
        <button
          class="ring-offset-background focus-visible:outline-hidden focus-visible:ring-ring inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&amp;_svg]:pointer-events-none [&amp;_svg]:size-4 [&amp;_svg]:shrink-0 border-input bg-background hover:bg-accent hover:text-accent-foreground border h-10 w-10"
          type="button"
          aria-haspopup="dialog"
          aria-expanded="false"
          aria-controls="radix-_r_1t_"
          data-state="closed"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="lucide lucide-filter h-4 w-4"
          >
            <polygon
              points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"
            ></polygon>
          </svg>
        </button>
      </div>
    </div>
    <div class="flex-1 relative">
      <el-scrollbar class="absolute inset-0">
        <div
          class="rounded-lg border bg-card text-card-foreground shadow-2xs"
        >
          <div class="flex flex-col space-y-1.5 p-6">
            <h3 class="text-2xl font-semibold leading-none tracking-tight">
              AI Dynamic Pricing
            </h3>
            <p class="text-sm text-muted-foreground">
              Configure AI-powered pricing strategies for your products.
            </p>
          </div>
          <div class="p-6 pt-0 space-y-6">
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div
                class="rounded-lg border bg-card text-card-foreground shadow-2xs"
                data-v0-t="card"
              >
                <div class="p-6 pt-6">
                  <div class="text-center">
                    <div class="text-2xl font-bold">3</div>
                    <p class="text-xs text-muted-foreground">
                      Active AI Pricing Models
                    </p>
                  </div>
                </div>
              </div>
              <div
                class="rounded-lg border bg-card text-card-foreground shadow-2xs"
                data-v0-t="card"
              >
                <div class="p-6 pt-6">
                  <div class="text-center">
                    <div class="text-2xl font-bold">24</div>
                    <p class="text-xs text-muted-foreground">
                      Products with Dynamic Pricing
                    </p>
                  </div>
                </div>
              </div>
              <div
                class="rounded-lg border bg-card text-card-foreground shadow-2xs"
                data-v0-t="card"
              >
                <div class="p-6 pt-6">
                  <div class="text-center">
                    <div class="text-2xl font-bold">+8.2%</div>
                    <p class="text-xs text-muted-foreground">
                      Revenue Increase from AI Pricing
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div class="space-y-4">
              <h3 class="text-lg font-medium">Pricing Models</h3>
              <div
                class="rounded-lg border bg-card text-card-foreground shadow-2xs"
                data-v0-t="card"
              >
                <div class="flex flex-col space-y-1.5 p-6 pb-2">
                  <div class="flex justify-between items-center">
                    <div>
                      <h3 class="text-2xl font-semibold leading-none tracking-tight">
                        Competitive Pricing Model
                      </h3>
                      <p class="text-sm text-muted-foreground">
                        Adjusts prices based on competitor data
                      </p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked="true"
                      data-state="checked"
                      value="on"
                      class="peer inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=unchecked]:bg-input"
                      id="competitive-model"
                    >
                      <span
                        data-state="checked"
                        class="pointer-events-none block h-5 w-5 rounded-full bg-background shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0"
                      ></span>
                    </button>
                  </div>
                </div>
                <div class="p-6 pt-0">
                  <div class="space-y-4">
                    <div class="grid grid-cols-4 gap-4">
                      <div class="col-span-1">
                        <label class="text-sm font-medium">Aggressiveness</label
                        ><span
                          dir="ltr"
                          data-orientation="horizontal"
                          aria-disabled="false"
                          class="relative flex w-full touch-none select-none items-center mt-2"
                          style="--radix-slider-thumb-transform: translateX(-50%)"
                          ><span
                            data-orientation="horizontal"
                            class="relative h-2 w-full grow overflow-hidden rounded-full bg-secondary"
                            ><span
                              data-orientation="horizontal"
                              class="absolute h-full bg-primary"
                              style="left: 0%; right: 35%"
                            ></span></span
                          ><span
                            style="
                              transform: var(--radix-slider-thumb-transform);
                              position: absolute;
                              left: calc(65% - 3px);
                            "
                            ><span
                              role="slider"
                              aria-valuemin="0"
                              aria-valuemax="100"
                              aria-orientation="horizontal"
                              data-orientation="horizontal"
                              tabindex="0"
                              class="block h-5 w-5 rounded-full border-2 border-primary bg-background ring-offset-background transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
                              data-radix-collection-item=""
                              aria-valuenow="65"
                              style=""
                            ></span></span
                        ></span>
                      </div>
                      <div class="col-span-1">
                        <label class="text-sm font-medium">Update Frequency</label
                        ><button
                          type="button"
                          role="combobox"
                          aria-controls="radix-_r_40_"
                          aria-expanded="false"
                          aria-autocomplete="none"
                          dir="ltr"
                          data-state="closed"
                          class="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&amp;&gt;span]:line-clamp-1"
                        >
                          <span style="pointer-events: none">Daily</span
                          ><svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            class="lucide lucide-chevron-down h-4 w-4 opacity-50"
                            aria-hidden="true"
                          >
                            <path d="m6 9 6 6 6-6"></path>
                          </svg>
                        </button>
                      </div>
                      <div class="col-span-2">
                        <label class="text-sm font-medium">Price Range</label>
                        <div class="flex items-center gap-2 mt-2">
                          <input
                            class="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 w-20"
                            placeholder="Min %"
                            value="-10"
                          /><span>to</span
                          ><input
                            class="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 w-20"
                            placeholder="Max %"
                            value="5"
                          /><span>% from base price</span>
                        </div>
                      </div>
                    </div>
                    <div class="flex justify-end">
                      <button
                        class="ring-offset-background focus-visible:outline-hidden focus-visible:ring-ring inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&amp;_svg]:pointer-events-none [&amp;_svg]:size-4 [&amp;_svg]:shrink-0 border-input bg-background hover:bg-accent hover:text-accent-foreground border h-10 px-4 py-2"
                      >
                        Configure
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div
                class="rounded-lg border bg-card text-card-foreground shadow-2xs"
                data-v0-t="card"
              >
                <div class="flex flex-col space-y-1.5 p-6 pb-2">
                  <div class="flex justify-between items-center">
                    <div>
                      <h3 class="text-2xl font-semibold leading-none tracking-tight">
                        Demand-Based Pricing
                      </h3>
                      <p class="text-sm text-muted-foreground">
                        Adjusts prices based on real-time demand
                      </p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked="true"
                      data-state="checked"
                      value="on"
                      class="peer inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=unchecked]:bg-input"
                      id="demand-model"
                    >
                      <span
                        data-state="checked"
                        class="pointer-events-none block h-5 w-5 rounded-full bg-background shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0"
                      ></span>
                    </button>
                  </div>
                </div>
                <div class="p-6 pt-0">
                  <div class="space-y-4">
                    <div class="grid grid-cols-4 gap-4">
                      <div class="col-span-1">
                        <label class="text-sm font-medium">Sensitivity</label
                        ><span
                          dir="ltr"
                          data-orientation="horizontal"
                          aria-disabled="false"
                          class="relative flex w-full touch-none select-none items-center mt-2"
                          style="--radix-slider-thumb-transform: translateX(-50%)"
                          ><span
                            data-orientation="horizontal"
                            class="relative h-2 w-full grow overflow-hidden rounded-full bg-secondary"
                            ><span
                              data-orientation="horizontal"
                              class="absolute h-full bg-primary"
                              style="left: 0%; right: 55%"
                            ></span></span
                          ><span
                            style="
                              transform: var(--radix-slider-thumb-transform);
                              position: absolute;
                              left: calc(45% + 1px);
                            "
                            ><span
                              role="slider"
                              aria-valuemin="0"
                              aria-valuemax="100"
                              aria-orientation="horizontal"
                              data-orientation="horizontal"
                              tabindex="0"
                              class="block h-5 w-5 rounded-full border-2 border-primary bg-background ring-offset-background transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
                              data-radix-collection-item=""
                              aria-valuenow="45"
                              style=""
                            ></span></span
                        ></span>
                      </div>
                      <div class="col-span-1">
                        <label class="text-sm font-medium">Data Source</label
                        ><button
                          type="button"
                          role="combobox"
                          aria-controls="radix-_r_41_"
                          aria-expanded="false"
                          aria-autocomplete="none"
                          dir="ltr"
                          data-state="closed"
                          class="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&amp;&gt;span]:line-clamp-1"
                        >
                          <span style="pointer-events: none">Sales Data</span
                          ><svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            class="lucide lucide-chevron-down h-4 w-4 opacity-50"
                            aria-hidden="true"
                          >
                            <path d="m6 9 6 6 6-6"></path>
                          </svg>
                        </button>
                      </div>
                      <div class="col-span-2">
                        <label class="text-sm font-medium">Price Range</label>
                        <div class="flex items-center gap-2 mt-2">
                          <input
                            class="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 w-20"
                            placeholder="Min %"
                            value="-15"
                          /><span>to</span
                          ><input
                            class="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 w-20"
                            placeholder="Max %"
                            value="20"
                          /><span>% from base price</span>
                        </div>
                      </div>
                    </div>
                    <div class="flex justify-end">
                      <button
                        class="ring-offset-background focus-visible:outline-hidden focus-visible:ring-ring inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&amp;_svg]:pointer-events-none [&amp;_svg]:size-4 [&amp;_svg]:shrink-0 border-input bg-background hover:bg-accent hover:text-accent-foreground border h-10 px-4 py-2"
                      >
                        Configure
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div
                class="rounded-lg border bg-card text-card-foreground shadow-2xs"
                data-v0-t="card"
              >
                <div class="flex flex-col space-y-1.5 p-6 pb-2">
                  <div class="flex justify-between items-center">
                    <div>
                      <h3 class="text-2xl font-semibold leading-none tracking-tight">
                        Time-Based Pricing
                      </h3>
                      <p class="text-sm text-muted-foreground">
                        Adjusts prices based on time of day/week
                      </p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked="false"
                      data-state="unchecked"
                      value="on"
                      class="peer inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=unchecked]:bg-input"
                      id="time-model"
                    >
                      <span
                        data-state="unchecked"
                        class="pointer-events-none block h-5 w-5 rounded-full bg-background shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0"
                      ></span>
                    </button>
                  </div>
                </div>
                <div class="p-6 pt-0">
                  <div class="space-y-4">
                    <div class="grid grid-cols-4 gap-4">
                      <div class="col-span-2">
                        <label class="text-sm font-medium">Schedule Type</label
                        ><button
                          type="button"
                          role="combobox"
                          aria-controls="radix-_r_42_"
                          aria-expanded="false"
                          aria-autocomplete="none"
                          dir="ltr"
                          data-state="closed"
                          class="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&amp;&gt;span]:line-clamp-1"
                        >
                          <span style="pointer-events: none">Daily Pattern</span
                          ><svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            class="lucide lucide-chevron-down h-4 w-4 opacity-50"
                            aria-hidden="true"
                          >
                            <path d="m6 9 6 6 6-6"></path>
                          </svg>
                        </button>
                      </div>
                      <div class="col-span-2">
                        <label class="text-sm font-medium">Price Range</label>
                        <div class="flex items-center gap-2 mt-2">
                          <input
                            class="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 w-20"
                            placeholder="Min %"
                            value="-5"
                          /><span>to</span
                          ><input
                            class="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 w-20"
                            placeholder="Max %"
                            value="10"
                          /><span>% from base price</span>
                        </div>
                      </div>
                    </div>
                    <div class="flex justify-end">
                      <button
                        class="ring-offset-background focus-visible:outline-hidden focus-visible:ring-ring inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&amp;_svg]:pointer-events-none [&amp;_svg]:size-4 [&amp;_svg]:shrink-0 border-input bg-background hover:bg-accent hover:text-accent-foreground border h-10 px-4 py-2"
                      >
                        Configure
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="space-y-4">
              <h3 class="text-lg font-medium">Train New AI Model</h3>
              <div
                class="rounded-lg border bg-card text-card-foreground shadow-2xs"
                data-v0-t="card"
              >
                <div class="p-6 pt-6">
                  <div class="space-y-4">
                    <div class="grid grid-cols-2 gap-4">
                      <div>
                        <label class="text-sm font-medium">Model Name</label
                        ><input
                          class="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 mt-1"
                          placeholder="Enter model name"
                        />
                      </div>
                      <div>
                        <label class="text-sm font-medium">Model Type</label
                        ><button
                          type="button"
                          role="combobox"
                          aria-controls="radix-_r_43_"
                          aria-expanded="false"
                          aria-autocomplete="none"
                          dir="ltr"
                          data-state="closed"
                          data-placeholder=""
                          class="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&amp;&gt;span]:line-clamp-1"
                        >
                          <span style="pointer-events: none">Select model type</span
                          ><svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            class="lucide lucide-chevron-down h-4 w-4 opacity-50"
                            aria-hidden="true"
                          >
                            <path d="m6 9 6 6 6-6"></path>
                          </svg>
                        </button>
                      </div>
                    </div>
                    <div>
                      <label class="text-sm font-medium">Data Sources</label>
                      <div class="grid grid-cols-2 gap-2 mt-1">
                        <div class="flex items-center space-x-2">
                          <button
                            type="button"
                            role="checkbox"
                            aria-checked="true"
                            data-state="checked"
                            value="on"
                            class="peer h-4 w-4 shrink-0 rounded-sm border border-primary ring-offset-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground"
                            id="data-sales"
                          >
                            <span
                              data-state="checked"
                              class="flex items-center justify-center text-current"
                              style="pointer-events: none"
                              ><svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="lucide lucide-check h-4 w-4"
                              >
                                <path d="M20 6 9 17l-5-5"></path>
                              </svg>
                            </span></button
                          ><label for="data-sales" class="text-sm"
                            >Sales History</label
                          >
                        </div>
                        <div class="flex items-center space-x-2">
                          <button
                            type="button"
                            role="checkbox"
                            aria-checked="true"
                            data-state="checked"
                            value="on"
                            class="peer h-4 w-4 shrink-0 rounded-sm border border-primary ring-offset-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground"
                            id="data-inventory"
                          >
                            <span
                              data-state="checked"
                              class="flex items-center justify-center text-current"
                              style="pointer-events: none"
                              ><svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="lucide lucide-check h-4 w-4"
                              >
                                <path d="M20 6 9 17l-5-5"></path>
                              </svg>
                            </span></button
                          ><label for="data-inventory" class="text-sm"
                            >Inventory Levels</label
                          >
                        </div>
                        <div class="flex items-center space-x-2">
                          <button
                            type="button"
                            role="checkbox"
                            aria-checked="true"
                            data-state="checked"
                            value="on"
                            class="peer h-4 w-4 shrink-0 rounded-sm border border-primary ring-offset-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground"
                            id="data-competitor"
                          >
                            <span
                              data-state="checked"
                              class="flex items-center justify-center text-current"
                              style="pointer-events: none"
                              ><svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="lucide lucide-check h-4 w-4"
                              >
                                <path d="M20 6 9 17l-5-5"></path>
                              </svg>
                            </span></button
                          ><label for="data-competitor" class="text-sm"
                            >Competitor Prices</label
                          >
                        </div>
                        <div class="flex items-center space-x-2">
                          <button
                            type="button"
                            role="checkbox"
                            aria-checked="false"
                            data-state="unchecked"
                            value="on"
                            class="peer h-4 w-4 shrink-0 rounded-sm border border-primary ring-offset-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground"
                            id="data-seasonal"
                          ></button
                          ><label for="data-seasonal" class="text-sm"
                            >Seasonal Trends</label
                          >
                        </div>
                        <div class="flex items-center space-x-2">
                          <button
                            type="button"
                            role="checkbox"
                            aria-checked="false"
                            data-state="unchecked"
                            value="on"
                            class="peer h-4 w-4 shrink-0 rounded-sm border border-primary ring-offset-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground"
                            id="data-customer"
                          ></button
                          ><label for="data-customer" class="text-sm"
                            >Customer Segments</label
                          >
                        </div>
                        <div class="flex items-center space-x-2">
                          <button
                            type="button"
                            role="checkbox"
                            aria-checked="false"
                            data-state="unchecked"
                            value="on"
                            class="peer h-4 w-4 shrink-0 rounded-sm border border-primary ring-offset-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground"
                            id="data-external"
                          ></button
                          ><label for="data-external" class="text-sm"
                            >External Factors</label
                          >
                        </div>
                      </div>
                    </div>
                    <div>
                      <label class="text-sm font-medium">Training Parameters</label
                      ><textarea
                        class="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 mt-1"
                        placeholder="Enter any specific parameters or notes for training"
                      ></textarea>
                    </div>
                    <div class="flex justify-end">
                      <button
                        class="ring-offset-background focus-visible:outline-hidden focus-visible:ring-ring inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&amp;_svg]:pointer-events-none [&amp;_svg]:size-4 [&amp;_svg]:shrink-0 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          class="lucide lucide-settings h-4 w-4 mr-2"
                        >
                          <path
                            d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"
                          ></path>
                          <circle cx="12" cy="12" r="3"></circle>
                        </svg>
                        Train Model
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </el-scrollbar>
    </div>
  </div>
</template>

<script>
export default {
  name: "AiPricingView",
  props: {
    usageSummary: {
      type: Object,
      default: () => ({ spend: "$640", credits: "2,340" }),
    },
  },
};
</script>

<style lang="scss" scoped>
.ai-pricing-view {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;

  h2 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
  }
}

.pricing-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
}

.pricing-card {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  padding: 16px;
  background: #fff;
  display: flex;
  flex-direction: column;
  gap: 8px;

  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
  }

  p {
    margin: 0;
    color: #4b5563;
    font-size: 14px;
  }

  .price {
    font-size: 24px;
    font-weight: 600;
    margin-top: 8px;
    color: #111827;
  }

  ul {
    margin: 0;
    padding-left: 18px;
    color: #6b7280;
    font-size: 13px;
  }
}

.usage-summary {
  margin-top: auto;
  padding-top: 24px;
  display: flex;
  gap: 32px;
  font-size: 14px;
  color: #4b5563;

  strong {
    margin-left: 4px;
    color: #111827;
  }
}
</style>
